import RangeInput from "./range.js";
import AccordionController from "./accordion.js";
import StickyColumn from "./stickyColumns.js";
import FontResizer from "./fontResizer.js";
import Menu from "./menu.js";
import AjaxPopup from "./ajaxPopup.js";
import FieldsetVisibilityController from "./fieldsetVisibility.js";
import InteractiveTime from "./interactiveTime.js";
import {ColorPaletteController, ColorPaletteItem} from "./colorPalette.js";
import ImageInputHandler from "./imageInputHandler.js";
import StepController from "./stepController.js";
import DataCollector from "./dataCollector.js";
import CanvasController from "./canvasController.js";
import StorageManager from "./storageManager.js";
import CodeHighlighter from "./codeHighlighter.js";
import Downloader from "./downloader.js";
import Notification from "./notification.js";
import InputSanitizer from "./inputSanitizer.js";

const DEMO_FILENAME = `faviconer-demo.svg`;
const MOBILE_QUERY = window.matchMedia("(max-width: 767.98px)");
const TABLET_QUERY = window.matchMedia("screen and (max-width: 991.98px)");

let steps = {};
let imageTools = {};
let storage = {};
let uiControllers = {};
let canvasController = null;
let downloader = null;
let notification = null;
let useRecommendedActive = false;
let wasDrawn = false;
const routes = ROUTES;

function pathToStep(path) {
  for (const [step, url] of Object.entries(routes)) {
    if (url.path === path) return step;
  }
  return "main";
}

document.addEventListener("DOMContentLoaded", () => {
  initApp();
});

function initApp() {
  // Initialize state storage
  storage = initStorage();

  // Initialize UI controllers (accordion, color picker, font resizer, etc.)
  uiControllers = initUIControllers();
  rebindUIControllers(uiControllers);

  // Handle image uploading and canvas interaction
  imageTools = initImageHandling({
    storage,
    onImageLoaded: () => {
      if(pathToStep(location.pathname) == "main"){
        clearState();
      }
      steps.stepController.show("confirm");
    },
    onImageProcessed: () => {
      saveState();
    }
  });

  // Set up step navigation and data collection
  steps = initStepsAndData({
    storage,
    imageTools,
    uiControllers,
    canvasController: imageTools.getCanvasController()
  });

  notification = new Notification({
    containerClass: "notify",
    itemClass: "notify__item",
    timeout: 10000,
    mods: {
      hidden: 'notify__item--hidden',
      error: 'notify__item--error'
    },
  });

  // Initialize header menu, popups, and interactive visuals
  initInterface({ mobileQuery: MOBILE_QUERY });
}

function initStorage() {
  const storage = new StorageManager({
    key: STORAGE_KEY,
    maxAge: MAX_JOB_AGE
  });

  return storage;
}

function clearState(){
  storage.clear();
}
async function saveState(){
  await storage.debounceSave({
    page: steps.stepController.active,
    ...steps.dataCollector.data,
    "image-offset": {
      x: imageTools.getCanvasController()?.offset?.x,
      y: imageTools.getCanvasController()?.offset?.y,
    },
    "image-real-scale": imageTools.getCanvasController()?.scale
  }, imageTools.imageInputHandler.blobImage);
}
if (document.readyState !== 'loading') {
  restoreState();
} else {
  document.addEventListener('DOMContentLoaded', () => restoreState());
}

let iter = 0;
let updatedConfirm = false;
let updatedCustomize = false;

async function restoreState(force = false) {
  // Exit if already restored
  if (updatedConfirm || updatedCustomize) return;

  const hash = ["#prev","#reloaded"].includes(location.hash);
  const path = location.pathname != "/" && Object.values(routes).map(route => route.path).includes(location.pathname);
  const restoreButton = document.querySelector("button[data-action='restore']");

  if(!restoreButton){
    const statusCode = document.querySelector("meta[name='status-code'")?.getAttribute("content");
    if (!statusCode) history.replaceState({},"","/");
    return;
  }

  // Exit if not forced and hash is not #prev
  if (!force && !hash && !path) return;
  restoreButton.disabled = true;

  // Wait for steps to be loaded
  const stepsLoaded = steps.stepController.steps.has("confirm") && steps.stepController.steps.has("customize") && steps.stepController.steps.has("result");
  
  if (!stepsLoaded) {
    if (iter >= 10) {
      if (location.hash !== "#reloaded") {
        location.hash = "#reloaded";
        window.location.reload();
      } else {
        restoreButton.classList.add("internal-link--error-text");
        if(["#prev","#reloaded"].includes(location.hash)){
          history.replaceState({},"","/");
        }
      }
      return;
    }

    setTimeout(() => restoreState(true), 100 + iter * 50);
    iter++;
    return;
  }

  if (location.hash === "#reloaded" || location.hash === "#prev") {
    history.replaceState(null, "", location.pathname + location.search);
  }

  const state = await storage.load();

  steps.stepController.setListener("update", name => {
    if (name !== "confirm" || updatedConfirm) return;

    updatedConfirm = true;

    applyState(state, ["image", "page"], null, {
      "image-offset": value => {
        canvasController.offset = value;
        canvasController.renderImage();
      }
    });
  });

  steps.stepController.setListener("show", name => {
    window.scrollTo(window.pageXOffset, 0);
    if (name !== "customize" || updatedCustomize) return;

    updatedCustomize = true;

    applyState(state, ["image", "image-offset", "image-scale", "page"]);

    updateOptionsStatus();
  });
  let stepName = pathToStep(location.pathname);
  stepName = stepName == "main" ? "confirm" : stepName;
  if(!state.image){
    notification?.add(DEFAULT_ERROR_TEXT || "", ["error"]);
    history.replaceState({},"","/");
    return;
  }
  imageTools.imageInputHandler.handleFiles([state.image], async () => {
    const currentStepName = pathToStep(location.pathname);
    if(currentStepName == stepName){
      return;
    }
    try{
      await new Promise((resolve, reject) => {
        const start = performance.now();
        const wait = () => {
          if (wasDrawn) return resolve();
          if (performance.now() - start > 5000) return reject("Timeout waiting for wasDrawn");
          requestAnimationFrame(wait);
        };
        wait();
      });
    } catch (e) {
      notification?.add(DEFAULT_ERROR_TEXT || "", ["error"]);
      history.replaceState({},"","/");
    }
    if(stepName == "result"){
      steps.stepController.show("customize");
    }
    steps.stepController.show(stepName);
  });

  function applyState(state, exclude = [], parentSelector = null, listeners = {}) {
    for (const key in state) {
      if (exclude.includes(key)) continue;

      const value = state[key];

      if (listeners[key]) {
        listeners[key](value);
        continue;
      }

      const selector = parentSelector
        ? `${parentSelector} [data-id='${key}']`
        : `[data-id='${key}']`;

      if (typeof value === "object" && value !== null) {
        applyState(value, [], selector); // recursive
        continue;
      }

      const input = document.querySelector(selector);
      if (!input) continue;

      if (["checkbox", "radio"].includes(input.type)) {
        if(input.checked != !!value){
          input.click();
        } else {
          const fieldset = document.querySelector(`[data-relative='${input.id}']`);
          if (fieldset) fieldset.disabled = !input.checked;
        }
      } else {
        input.value = value;
        input.dispatchEvent(new Event("input"));
        input.dispatchEvent(new Event("change"));
      }
    }
    restoreButton.disabled = false;
  }
}

function initUIControllers() {
  // Accordion for toggling details
  const accordion = new AccordionController({
    selectors: {
      details: "details.options",
      header: "summary"
    }
  });

  const paletteSetColor = ColorPaletteItem.prototype.updateUI;

  ColorPaletteItem.prototype.updateUI = function(...args) {
    const oldValue = this.input.value;
    paletteSetColor.apply(this, args);
    if(this.input.value != oldValue){
      updateOptionsStatus();
    }
  };

  // Color palette component
  const colorPalette = new ColorPaletteController({
    selectors: {
      input: ".color__input",
      textInput: "input[type='text']",
      element: ".color",
      area: "[data-elem='area']",
      hue: "[data-elem='hue']",
      brightness: "[data-elem='brightness']",
      saturation: "[data-elem='saturation']"
    },
    template: `
      <div class="palette color__palette">
        <div class="palette__color" data-elem="area">
          <input class="palette__color-input" data-elem="saturation" type="range" min="0" max="100" step=".01" value="50">
          <input class="palette__color-input" data-elem="brightness" type="range" min="0" max="100" step=".01" value="50" tabindex="-1">
          <div class="palette__color-thumb" data-elem="thumb"></div>
        </div>
        <div class="palette__hue">
          <input class="palette__hue-input" data-elem="hue" type="range" min="0" max="360" step="1" value="120">
        </div>
      </div>
    `,
    classNames: {
      visible: "palette--visible",
      areaInputActive: "palette__color-input--focused",
      hueInputActive: "palette__hue-input--focused"
    },
    appendTo: ".color"
  });


  // Fieldset visibility toggler
  const fieldsetVisibility = new FieldsetVisibilityController();

  // Font resizer for preview
  const fontResizer = new FontResizer({
    selectors: {
      block: "[data-standart-width]"
    },
    widthAttribute: "data-standart-width"
  });

  // Range inputs
  const rangeInput = new RangeInput({
    selectors: {
      input: ".range__input"
    }
  });

  // Sticky column behavior
  const stickyColumn = new StickyColumn({
    selectors: {
      column: ".options__column--sticky-left, .options__column--sticky-right",
      except: {
        top: [
          { selector: '.page__header' }
        ],
        bottom: [
          { selector: '.subfooter', closest: '.page__main' }
        ]
      }
    },
    offset: {
      top: 16,
      bottom: 16,
    },
    mobileQuery: TABLET_QUERY
  });

  // Interactive time display (decorative / UX-related)
  const interactiveTimeController = new InteractiveTime({
    selectors: {
      element: ".interact-time"
    }
  });

  // Store for future re-binding
  return {
    rangeInput,
    accordion,
    stickyColumn,
    fontResizer,
    colorPalette,
    fieldsetVisibility,
    interactiveTimeController
  };
}

function rebindUIControllers(ui) {
  ui.rangeInput.bindNew();
  ui.accordion.bindNew();
  ui.stickyColumn.bindNew();
  ui.fontResizer.bindNew();
  ui.colorPalette.bindNew();
  ui.fieldsetVisibility.bindNew();
  ui.interactiveTimeController.bindNew();
}

function updateOptionsStatus() {
  for (const detailsElem of document.querySelectorAll(".options")) {
    const status = detailsElem.querySelector(".options-header__status");
    if (!status) continue;
    status.classList.toggle("options-header__status--visible", hasChanges(detailsElem));
  }

  function hasChanges(root) {
    for (const el of root.querySelectorAll("input[data-id], select[data-id], textarea[data-id]")) {
      if (isChanged(el)) return true;
    }
    return false;
  }

  function isChanged(el) {
    if (el.type === "checkbox" || el.type === "radio") return el.checked !== el.defaultChecked;
    if (el.tagName === "SELECT") return [...el.options].some(o => o.selected !== o.defaultSelected);
    return el.value !== el.defaultValue;
  }
}

function initImageHandling({ storage, onImageLoaded, onImageProcessed }) {
  const imageInputHandler = new ImageInputHandler({
    minWidth: 48,
    minHeight: 48,
    demoFileName: DEMO_FILENAME,
    selectors: {
      input: "#upload-image",
      label: "label[for='upload-image']"
    },
    classNames: {
      drop: "upload-form__label--drop"
    },
    onImageLoaded: (file, img) => {
      // Custom external callback
      onImageLoaded?.();

      // Create canvas controller if needed
      const canvas = document.querySelector("#image-canvas");
      if(!canvas){
        return;
      }
      const imageSizeLabel = document.querySelector("#original-image-size");

      if (!canvasController) {
        canvasController = new CanvasController(canvas, {
          clampMargin: 20,
          classNames: {
            move: "confirm-image__canvas--move"
          },
          onStop: () => {
            saveState();
          },
          onDraw: () => {
            wasDrawn = true;
          }
        });
      }

      // Load image into canvas
      canvasController.loadFromFile(file, () => {
        imageSizeLabel.innerHTML = `${img.width}&times;${img.height}px`;
        steps.stepController.update("confirm");
        saveState();
      });

      // Zoom controls
      const scaleInput = document.querySelector("#original-image-scale");
      if (scaleInput) {
        canvasController.attachZoomWithWheel(scaleInput);
      }

      document.querySelector("[data-action='reset-scale']")?.click();

      // Custom external callback
      onImageProcessed?.();
    },
    onInvalidImage: errorText => {
      notification?.add(errorText || DEFAULT_ERROR_TEXT || "", ["error"]);
    }
  });

  return {
    imageInputHandler,
    getCanvasController: () => canvasController
  };
}

function initStepsAndData({ storage, imageTools, uiControllers }) {
  const page = document.querySelector(".page");
  const dataCollector = new DataCollector(page, (path, value) => {
    if (path === 'image-scale' && canvasController){
      canvasController.zoomRelativeToCover(value);
    }

    saveState();

    const elements = document.querySelectorAll("[data-image-example]");
    if(!elements.length){
      return;
    }
    updateImageOptions("desktop", "svg");
    updateImageOptions("ios");
    updateImageOptions("android");
    updateImageOptions("svg", "desktop");
  });

  const stepController = new StepController({
    container: ".page",
    onShow: async (name, element) => {
      // If customizing, update image preview
      if (name === "customize") {
        const startTime = performance.now();
        const check = async () => {
          if (wasDrawn && canvasController) {
            const blob = await canvasController.toBlob();
            const url = URL.createObjectURL(blob);
            document.querySelectorAll("[data-image-example]").forEach(el => {
              el.style.setProperty("--u", `url(${url})`);
            });
            return;
          }
          if (performance.now() - startTime > 5000) {
            notification?.add(DEFAULT_ERROR_TEXT, ["error"]);
            return;
          }
          requestAnimationFrame(check);
        };
        requestAnimationFrame(check);
      }      

      // Re-bind UI to new step content
      rebindUIControllers(uiControllers);

      // Bind fields to data collector
      dataCollector.bindNewFields(element);

      // Save updated state
      saveState();
    },
    onLoad: (name, step) => {
      if(name != "customize") return;
      new InputSanitizer(
        step.querySelector('#ios-app-name'),
        'app-name',
        step.querySelector('#ios-app-name-hint'),
        hint => hint.classList.remove("tip--hidden")
      );
      new InputSanitizer(
        step.querySelector('#android-app-name'),
        'app-name',
        step.querySelector('#android-app-name-hint'),
        hint => hint.classList.remove("tip--hidden")
      );
      new InputSanitizer(
        step.querySelector('#android-short-name'),
        'short-name',
        step.querySelector('#android-short-name-hint'),
        hint => hint.classList.remove("tip--hidden")
      );
      new InputSanitizer(
        step.querySelector('#android-start-page'),
        'start-page',
        step.querySelector('#android-start-page-hint'),
        hint => hint.classList.remove("tip--hidden")
      );
      new InputSanitizer(
        step.querySelector('#general-app-name'),
        'app-name',
        step.querySelector('#general-app-name-hint'),
        hint => hint.classList.remove("tip--hidden")
      );
      new InputSanitizer(
        step.querySelector('#general-path'),
        'path',
        step.querySelector('#general-path-hint'),
        hint => hint.classList.remove("tip--hidden")
      );
      new InputSanitizer(
        step.querySelector('#general-version'),
        'version',
        step.querySelector('#general-version-hint'),
        hint => hint.classList.remove("tip--hidden")
      );      
    },
    onError: (errorText)=>{
      if(!DEFAULT_ERROR_TEXT) return;
      notification?.add(DEFAULT_ERROR_TEXT, ["error"]);
    }
  });

  
  let historyPopped = false;
  stepController.setListener("show", name => {
    window.scrollTo(window.pageXOffset, 0);
    const path = routes[name]?.path;
    if(!path){
      return;
    }
    if(!historyPopped){
      history.pushState({},"",path);
      return;
    }
    historyPopped = false;
  });
  const job = new Downloader({
    statusPath: (id) => `${DOWNLOADER_SETTINGS.statusPath}${id}`,
    downloadPath: (id) => `${DOWNLOADER_SETTINGS.downloadPath}${id}`,
    pollInterval: DOWNLOADER_SETTINGS.pollInterval,
    maxWait: DOWNLOADER_SETTINGS.maxWait,
  });
  downloader = job;
  let startTime = null;
  let showTimeoutId = null;
  job.addEventListener("started", e => toggleResultDisabledElems(true));
  /* job.addEventListener("status",  e => {}); */
  job.addEventListener("ready",   e => {
    const diff = new Date() - startTime;
    const avgTime = AVG_RESULT_WAITING_TIME + Math.floor(Math.random() * 2000 - 500);
    const timeout = avgTime - diff;
    showTimeoutId = setTimeout(showResults, timeout);;
  });
  job.addEventListener("error",   e => {errorResults(e);});
  job.addEventListener("timeout", e => {errorResults(e);});
  function errorResults(e){
    toggleResultLoaderErrorState(true);
    clearTime();
  }
  function showResults(){
    toggleResultDisabledElems(false);
    clearTime();
  }
  function clearTime(){
    startTime = null;
    clearTimeout(showTimeoutId);
    showTimeoutId = null;
  }
  function setTitle(mode){
    let resultBlock = document.querySelector("#resultBlock");
    let mainTitle = resultBlock.querySelector(".content__title");
    const isError = mode == "isError";
    const isProcessing = mode == "isProcessing";
    if(isError){
      mainTitle.innerHTML = mainTitle.dataset.errorText || "";
    } else if(isProcessing){
      mainTitle.innerHTML = mainTitle.dataset.processText || "";
    } else {
      mainTitle.innerHTML = mainTitle.dataset.baseText || "";
    }
  }
  function toggleResultLoaderErrorState(showError){
    let loader = resultBlock.querySelector(".content-loader");
    loader.classList.toggle("content-loader--error", showError);
    setTitle(showError ? "isError" : null);
  }
  function toggleResultDisabledElems(isProcessing){
    let resultBlock = document.querySelector("#resultBlock");
    let loader = resultBlock.querySelector(".content-loader");
    let subfooterDownloadButton = resultBlock.querySelector(".subfooter [data-action='download']");
    let codeBox = resultBlock.querySelector(".box__code");
    let links = resultBlock.querySelector(".options-text--links");

    loader.classList.toggle("content-loader--hidden", !isProcessing);
    toggleResultLoaderErrorState(false);
    subfooterDownloadButton.classList.toggle("button--disabled", isProcessing);
    setTitle(isProcessing ? "isProcessing" : null);

    if(job?.status?.html){
      codeBox.innerText = job.status.html.trim();
    }
    new CodeHighlighter(codeBox);
    if(job?.status?.urls){
      let spans = links.querySelectorAll("span");
      if(spans.length == 2){
        spans[0].innerText = job.status.urls.uploadTo;
        spans[1].innerText = job.status.urls.mainIcon;
      }
    }
  }
  stepController.setListener("show", async name => {
    if(name != "result"){
      job.abort()
      return;
    }
    toggleResultDisabledElems(true);
    startTime = new Date();
    let state = await storage.load();
    let image = state.image;
    delete state.image;
    if(useRecommendedActive){
      state = Object.assign({}, steps.dataCollector.defaultData, {
        "image-offset" : {
          x: state["image-offset"].x,
          y: state["image-offset"].y
        },
        "image-scale" : state["image-scale"],
        "image-real-scale" : state["image-real-scale"]
      });
      useRecommendedActive = false;
    }
    try{
      await new Promise((resolve, reject) => {
        const start = performance.now();
        const wait = () => {
          if (wasDrawn) return resolve();
          if (performance.now() - start > 5000) return reject("Timeout waiting for wasDrawn");
          requestAnimationFrame(wait);
        };
        wait();
      });
    } catch (e) {
      notification?.add(DEFAULT_ERROR_TEXT || "", ["error"]);
    }
    const croppedCanvasBlob = await canvasController?.toBlob();
    let isSvg = image.type === 'image/svg+xml' || image.name.toLowerCase().endsWith('.svg');
    let text = null;
    if(isSvg){
      try {
        text = await image.text();
        const looksLikeSvg = text.includes('<svg') && text.includes('</svg>');
        isSvg = looksLikeSvg;
      } catch {
        isSvg = false;
      }
    }
    if (croppedCanvasBlob) {
      const ext = croppedCanvasBlob.type.split('/')[1]; // webp or png
      const croppedImage = new File([croppedCanvasBlob], `image.${ext}`, { type: croppedCanvasBlob.type });
      if(croppedImage.size < image.size || isSvg){
        image = croppedImage;
        if(!isSvg){
          state["image-offset"].x = 0;
          state["image-offset"].y = 0;
          state["image-scale"] = 1;
          state["image-real-scale"] = 1;
        } else {
          state["raster-image-offset"] = {};
          state["raster-image-offset"].x = 0;
          state["raster-image-offset"].y = 0;
          state["raster-image-scale"] = 1;
          state["raster-image-real-scale"] = 1;
        }
      }
    }
    await job.start({ state, imageFile: image, svgContent: isSvg ? text : null });
    try {
      await job.waitReady();
    } catch (err) {}
  });
  
  window.addEventListener("popstate", ()=>{
    const name = pathToStep(location.pathname);
    if(!stepController.steps.has(name)){
      return;
    }
    historyPopped = true;
    stepController.show(name);
  });

  const local = document.querySelector('.page__main');
  stepController.add('main', local);

  (async()=>{
    for(const key in routes){
      let url = routes[key]?.templatePath;
      if(!url) continue;
      url += "?h=" + BUILD_HASH;
      await stepController.load(key, url, '.page__main');
    }
  })();

  function updateImageOptions(key, addKey) {
    const options = dataCollector.data[key];
    const parent = [document.querySelector(`[data-id='${key}']`)];
    if(addKey){
      parent.push(document.querySelector(`[data-id='${addKey}']`));
    }

    if (key === "android") {
      const input = dataCollector.getInputByPath([key, "short-name"]);
      const name = options["short-name"] || options["app-name"] || input.dataset.default || input.placeholder;
      const el = document.querySelector(`[data-name-example='${key}']`);
      if (el && !isDisabled(input)) el.innerText = name;
    }

    if (key === "ios") {
      const input = dataCollector.getInputByPath([key, "app-name"]);
      const name = options["app-name"] || input.dataset.default || input.placeholder;
      const el = document.querySelector(`[data-name-example='${key}']`);
      if (el && !isDisabled(input)) el.innerText = name;
    }

    setCSS("--s", [key, "icon", "size"], parent, 1);
    setCSS("--r", [key, "icon", "corner-radius"], parent);
    // create fallback for background color and set below. path set to null
    setCSS("--bgc", [key, "background", "color"], parent, key == "desktop" ? "transparent" : "#fff", key != "desktop");
    setCSS("--bgr", [key, "background", "radius"], parent);
    
    // dark-icon
    if(key == "svg"){
      setCSS("--fi", [key, "filters", "invert"], parent);
      setCSS("--fb", [key, "filters", "brightness"], parent);
      setCSS("--fc", [key, "filters", "contrast"], parent);
      setCSS("--fs", [key, "filters", "saturation"], parent);
      setCSS("--fh", [key, "filters", "hue"], parent);
    }

    function setCSS(variable, path, element, fallback = "", useDefault = true) {
      element = Array.isArray(element) ? element : [element];
      const input = dataCollector.getInputByPath(path);
      if (!input || !element) return;
      const value = isDisabled(input) ? (useDefault ? input.defaultValue : fallback) : input.value;
      element.forEach(el => el.style.setProperty(variable, value));
    }

    function isDisabled(input) {
      if (input.disabled) return true;
      const fieldset = input.closest("fieldset[data-relative]");
      const relativeId = fieldset?.dataset.relative;
      const relativeInput = relativeId && document.querySelector("#" + relativeId);
      return relativeInput ? !relativeInput.checked : false;
    }
  }

  document.addEventListener("input", ({ target }) => {
    if (
      target.dataset.id !== "app-name" ||
      target.closest("[data-id='ios']")
    ) return;
  
    const a = document.querySelector("[data-id='android'] [data-id='app-name']");
    const g = document.querySelector("[data-id='general'] [data-id='app-name']");
    if (a.value === g.value) return;
  
    if (target === a) {
      g.value = target.value;
      g.dispatchEvent(new Event("input"));
    }
    if (target === g) {
      a.value = target.value;
      a.dispatchEvent(new Event("input"));
    }
  });

  document.addEventListener("change", (e) => { updateOptionsStatus(); });

  return { stepController, dataCollector };
}

function initInterface({ mobileQuery }) {
  // Restore button
  // After storage expires, the button shows an informational message.
  (() => {
    const baseKey = STORAGE_KEY;
    const maxAge = MAX_JOB_AGE || 3600000;
    const btn = document.querySelector("#restore-button");
    if (!btn) return;
  
    const minText = btn.querySelector("#restore-button-min");

    const getCurrentSession = () => {
      const key = `${baseKey}:${window.name}`;
      try {
        const data = JSON.parse(localStorage.getItem(key) || "{}");
        return data?.savedAt ? { k: key, data } : null;
      } catch {
        return null;
      }
    };  
  
    const getSessions = () => {
      return Object.keys(localStorage)
        .filter(k => k.startsWith(baseKey + ":") && !k.endsWith(":image"))
        .map(k => ({ k, data: JSON.parse(localStorage.getItem(k) || "{}") }))
        .filter(o => o.data?.savedAt);
    };
  
    const getLastSession = (sessions) => {
      return sessions.sort((a, b) => b.data.savedAt - a.data.savedAt)[0] || null;
    };
  
    const markExpired = () => {
      clearState();
      const title = btn.dataset.expiredTitle;
      const text = btn.dataset.expiredText;
      if (!title || !text) return btn.remove();
  
      btn.querySelector(".internal-link__title").innerHTML = title;
      btn.querySelector(".internal-link__text").innerHTML = text;
      btn.classList.add("internal-link--expired");
      btn.disabled = true;
    };
  
    const updateTimer = (savedAt) => {
      const elapsed = Date.now() - savedAt;
      if (elapsed >= maxAge) {
        clearInterval(id);
        markExpired();
      } else {
        minText.innerText = Math.ceil(elapsed / 60000);
      }
    };
  
    const current = getCurrentSession();
    const sessions = getSessions();
    const active = current || getLastSession(sessions);
    if (!sessions.length) return btn.remove();
  
    if (!active) return btn.remove();
    const { savedAt } = active.data;
  
    const id = setInterval(() => {
      if (!btn) {
        clearState();
        clearInterval(id);
        return;
      }
      updateTimer(savedAt);
    }, 1000);
  })();
  

  // Header menu toggle (burger + menu list)
  new Menu({
    selectors: {
      menuList: ".header__menu",
      button: ".header__button"
    },
    classNames: {
      init: "menu--init",
      open: "menu--visible",
      buttonActive: "header__button--toggled"
    },
    mobileQuery
  });

  // AJAX-powered popup handler
  new AjaxPopup({
    template: `
      <section class="popup">
        <div class="popup__inner">
          <div class="popup__header">
            <p class="popup__title"></p>
            <button class="popup__close"></button>        
          </div>
          <div class="popup__content" data-content-slot>
          </div>
        </div>
      </section>
    `,
    selectors: {
      parent: ".popup",
      openerLink: "[data-popup]",
      closeButton: ".popup__close"
    },
    classNames: {
      init: "popup--init",
      open: "popup--visible"
    },
    parser: {
      selector: "[data-popup-content]",
      to: "[data-content-slot]"
    },
    bodyScroll: {
      mediaQuery: mobileQuery
    },
    onError: (errorText)=>{
      notification?.add(errorText || DEFAULT_ERROR_TEXT || "", ["error"]);
    }
  });

  const menu = document.querySelector(".header__menu");
  const menuItemClassNames = ["menu__item", "menu__item--mobile--visible"];
  document.querySelectorAll(".footer__container > *").forEach(el => {
    const clone = el.cloneNode(true);
    const li = document.createElement("li");
    menuItemClassNames.forEach(cls => li.classList.add(cls));
    clone.setAttribute("class", clone.getAttribute("class").replaceAll("footer", "menu"));
    li.appendChild(clone);
    menu.appendChild(li);
  });

  document.addEventListener("click", event => {
    let target = event.target.closest("button[data-action]");
    if(!target){
      return;
    }
    event.preventDefault();
    const action = target.dataset.action;
    let copyButtonTimeout = null;
    switch(action){
      case "demo": {
        clearState();
        imageTools.imageInputHandler.loadFromSVG(DEMO_SVG);
        break;
      }
      case "change-image": {
        document.querySelector("#upload-image")?.click();
        break;
      }
      case "manual-setup": {
        // If the first accordion is closed, open it.
        // If the last accordion is out of the viewport, scroll to the first.
        const details = document.querySelectorAll("details.options");
        const firstSetupBlock = details[0];
        const lastSetupBlock = details[details.length - 1];
        const lastIsVisible = isInViewport(lastSetupBlock);
        if(!firstSetupBlock.open){
          firstSetupBlock.querySelector("summary").click();
        }
        if(!lastIsVisible){
          const header = document.querySelector(".header");
          const headerStyles = header && window.getComputedStyle(header) || {};
          const height = parseInt(headerStyles.height) || 0;
          const marginBottom = parseInt(headerStyles.marginBottom) || 0;
          const offset = (height + marginBottom);
          const top = firstSetupBlock.getBoundingClientRect().top + window.pageYOffset - offset;
          window.scrollTo({ top, behavior: 'smooth' });
        }
        break;
      }
      case "reset-scale": {
        const scaleInput = document.querySelector("#original-image-scale");
        scaleInput.value = scaleInput.getAttribute("value");
        scaleInput.dispatchEvent(new Event('input'));
        break;
      }
      case "reset-position": {
        canvasController.resetPosition();
        break;
      }
      case "reset-changes": {
        event.stopPropagation();
        const options = target.closest(".options")?.querySelector(".options__content");
        if(!options){
          return;
        }
        options.querySelectorAll("input, select, textarea").forEach(el => {
          if(el.closest(".palette")){
            return;
          }
          if (el.type === "checkbox" || el.type === "radio") {
            if (el.checked !== el.defaultChecked){
              el.click();
              el.checked = el.defaultChecked;
              el.dispatchEvent(new Event("change"));
            }
          } else if (el.tagName === "SELECT") {
            [...el.options].forEach(o => o.selected = o.defaultSelected);
            el.dispatchEvent(new Event("change", { bubbles: true }));
          } else {
            if (el.value !== el.defaultValue) {
              el.value = el.defaultValue;
              el.dispatchEvent(new Event("change", { bubbles: true }));
              el.dispatchEvent(new Event("input", { bubbles: true }));
            }
          }
        });
        updateOptionsStatus();
        
        break;
      }
      case "copy-code": {
        const codeElem = document.querySelector("#result-code");
        navigator.clipboard.writeText(codeElem.innerText);

        target.classList.add("text-button--data-text");
        target.disabled = true;

        clearTimeout(copyButtonTimeout);
        copyButtonTimeout = null;

        copyButtonTimeout = setTimeout(() => {
          target.classList.remove("text-button--data-text");
          target.disabled = false;
        }, 3000);
        break;
      }
      case "use-recommended": {
        useRecommendedActive = true;
        steps.stepController.show("result");
        break;
      }
      case "step-confirm":
      case "step-customize":
      case "step-result": {
        steps.stepController.show(action.replace("step-", ""));
        break;
      }
      case "restore": {
        restoreState(true);
        break;
      }
      case "download": {
        downloader.download("faviconer.zip");
        break;
      }
      default: {
        return;
      }
    }
  }, true);

  function isInViewport(el) {
    const rect = el.getBoundingClientRect()
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= window.innerHeight &&
      rect.right <= window.innerWidth
    )
  }
}
